import { X } from 'lucide-react';
import { useId, useRef, type ReactNode, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import { cx } from '../../lib/cx.ts';
import { useBodyScrollLock, useFocusTrap } from '../../lib/hooks.ts';
import { IconButton } from './Button.tsx';
import { usePresence } from './usePresence.ts';
import './Modal.css';

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
  footer?: ReactNode;
  size?: 'sm' | 'md' | 'lg';
  /** Element to focus when opened (defaults to the first focusable element). */
  initialFocus?: RefObject<HTMLElement | null>;
  closeOnBackdrop?: boolean;
  hideClose?: boolean;
  role?: 'dialog' | 'alertdialog';
  className?: string;
}

/** Dialog with focus trap, Esc to close, focus restore, backdrop fade 160ms and panel scale .98 → 1. */
export function Modal({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  size = 'md',
  initialFocus,
  closeOnBackdrop = true,
  hideClose = false,
  role = 'dialog',
  className,
}: ModalProps) {
  const { mounted, visible } = usePresence(open, 220);
  const panelRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const descriptionId = useId();
  useFocusTrap(panelRef, open && mounted, { onEscape: onClose, initialFocus });
  useBodyScrollLock(mounted);

  if (!mounted) return null;

  return createPortal(
    <div className="modal-root" data-state={visible ? 'open' : 'closed'}>
      <div className="modal-backdrop" aria-hidden="true" onClick={closeOnBackdrop ? onClose : undefined} />
      <div
        ref={panelRef}
        role={role}
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description ? descriptionId : undefined}
        tabIndex={-1}
        className={cx('modal-panel', `modal-panel--${size}`, className)}
      >
        <header className="modal-header">
          <div className="modal-heading">
            <h2 id={titleId} className="modal-title">
              {title}
            </h2>
            {description && (
              <p id={descriptionId} className="modal-description">
                {description}
              </p>
            )}
          </div>
          {!hideClose && <IconButton icon={X} label="ปิด" size="sm" tooltip={false} onClick={onClose} />}
        </header>
        {children !== undefined && children !== null && children !== false && <div className="modal-body">{children}</div>}
        {footer && <footer className="modal-footer">{footer}</footer>}
      </div>
    </div>,
    document.body,
  );
}
